import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import Toast from "cogo-toast";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ReferenceLine
} from "recharts";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {apiGetDeviceChart} from "./Actions";

const TIME_OPTIONS = [
    {label: "10 min", value: 10},
    {label: "1 hodina", value: 60},
    {label: "24 hodin", value: 1440},
];

const STATUS_LABEL = (v) => v === 1 ? "OK" : v === 0 ? "Chyba" : "—";

const CustomTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;
    const time = label ? new Date(label).toLocaleTimeString("cs-CZ") : "—";
    return (
        <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:"8px",padding:"10px 14px",fontSize: "var(--enigoo-fs-sm)",boxShadow:"0 4px 12px rgba(0,0,0,0.08)"}}>
            <div style={{fontWeight:700,marginBottom:"6px",color:"#475569"}}>{time}</div>
            {payload.map((p, i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",gap:"16px",color:p.color}}>
                    <span>{p.name}</span>
                    <span style={{fontWeight:600}}>
                        {p.name === "Baterie (%)" ? (p.value != null ? p.value + "%" : "—") :
                         p.name === "Baterie (min)" ? (p.value != null ? "~" + p.value + " min" : "—") :
                         STATUS_LABEL(p.value)}
                    </span>
                </div>
            ))}
        </div>
    );
};

const StatusYAxis = ({value}) => STATUS_LABEL(value);

const DeviceStatusChart = ({deviceId}) => {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [minutes, setMinutes] = useState(10);
    const user = useSelector(s => s.user);

    useEffect(() => {
        if (deviceId && user.activeAreal) loadData();
    }, [deviceId, minutes, user.activeAreal]);

    const loadData = () => {
        setLoading(true);
        apiGetDeviceChart(user.activeAreal.id, deviceId, minutes, (response) => {
            const rows = (response?.labels ?? []).map((label, i) => ({
                label,
                battery:     response.battery?.[i] ?? null,
                batteryTime: response.batteryTime?.[i] ?? null,
                nfc:         response.nfc?.[i] ?? null,
                barcode:     response.barcode?.[i] ?? null,
                network:     response.network?.[i] ?? null,
            }));
            setData(rows);
            setLoading(false);
        }, () => {
            Toast.error("Nepodařilo se načíst historii zařízení.");
            setLoading(false);
        });
    };

    const hasBattery = data?.some(r => r.battery != null);
    const hasStatus  = data?.some(r => r.nfc != null || r.barcode != null || r.network != null);

    const formatTick = (label) => {
        try { return new Date(label).toLocaleTimeString("cs-CZ", {hour:"2-digit", minute:"2-digit"}); }
        catch { return label; }
    };

    return (
        <div>
            {/* Time selector */}
            <div style={{display:"flex",gap:"8px",marginBottom:"16px"}}>
                {TIME_OPTIONS.map(o => (
                    <button key={o.value} onClick={() => setMinutes(o.value)} style={{
                        padding:"5px 14px", borderRadius:"6px", border:"1px solid",
                        borderColor: minutes === o.value ? "#2563eb" : "#e2e8f0",
                        background:  minutes === o.value ? "#eff6ff" : "#fff",
                        color:       minutes === o.value ? "#2563eb" : "#64748b",
                        fontWeight:  minutes === o.value ? 700 : 400,
                        fontSize: "var(--enigoo-fs-sm)", cursor:"pointer",
                    }}>{o.label}</button>
                ))}
                <button onClick={loadData} style={{
                    marginLeft:"auto", padding:"5px 14px", borderRadius:"6px",
                    border:"1px solid #e2e8f0", background:"#fff",
                    color:"#64748b", fontSize: "var(--enigoo-fs-sm)", cursor:"pointer",
                }}>↻ Obnovit</button>
            </div>

            {loading ? <DefaultLoader text="Načítám historii..."/> : !data?.length ? (
                <div style={{padding:"40px",textAlign:"center",color:"#94a3b8",fontSize: "var(--enigoo-fs-md)"}}>
                    Žádná data pro zvolené období
                </div>
            ) : (
                <>
                    {/* Battery % + time */}
                    {hasBattery && (
                        <div style={{border:"1px solid #e2e8f0",borderRadius:"12px",background:"#fff",padding:"16px",marginBottom:"12px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                            <div style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"14px"}}>
                                Baterie
                            </div>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={data} margin={{top:4,right:16,left:0,bottom:4}}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                    <XAxis dataKey="label" tickFormatter={formatTick} tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false}/>
                                    <YAxis yAxisId="pct" domain={[0,100]} tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v => v+"%"}/>
                                    <YAxis yAxisId="min" orientation="right" tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v => v+"m"}/>
                                    <Tooltip content={<CustomTooltip/>}/>
                                    <Legend iconType="circle" wrapperStyle={{fontSize:11}}/>
                                    <ReferenceLine yAxisId="pct" y={20} stroke="#fca5a5" strokeDasharray="4 4"/>
                                    <Line yAxisId="pct" type="monotone" dataKey="battery" name="Baterie (%)" stroke="#3b82f6" dot={false} strokeWidth={2} connectNulls/>
                                    <Line yAxisId="min" type="monotone" dataKey="batteryTime" name="Baterie (min)" stroke="#93c5fd" dot={false} strokeWidth={1.5} strokeDasharray="4 2" connectNulls/>
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* NFC / QR / Network */}
                    {hasStatus && (
                        <div style={{border:"1px solid #e2e8f0",borderRadius:"12px",background:"#fff",padding:"16px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                            <div style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"14px"}}>
                                Stav komponent (1 = OK, 0 = chyba)
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                                <LineChart data={data} margin={{top:4,right:16,left:0,bottom:4}}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                    <XAxis dataKey="label" tickFormatter={formatTick} tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false}/>
                                    <YAxis domain={[-0.2, 1.2]} ticks={[0,1]} tickFormatter={StatusYAxis} tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false}/>
                                    <Tooltip content={<CustomTooltip/>}/>
                                    <Legend iconType="circle" wrapperStyle={{fontSize:11}}/>
                                    <Line type="stepAfter" dataKey="nfc"     name="NFC"  stroke="#10b981" dot={false} strokeWidth={2} connectNulls/>
                                    <Line type="stepAfter" dataKey="barcode" name="QR"   stroke="#f59e0b" dot={false} strokeWidth={2} connectNulls/>
                                    <Line type="stepAfter" dataKey="network" name="Síť"  stroke="#8b5cf6" dot={false} strokeWidth={2} connectNulls/>
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DeviceStatusChart;
